import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import type { Profile } from '@/api/admin/profiles';
import { profileApi } from '@/api/admin/profiles';
import { ProfileAuditModal } from '@/components/modal/admin/profile/audit-modal';
import { ProfileUsersModal } from '@/components/modal/admin/profile/users-modal';
import { ProfileTable } from '@/components/table/admin/profile-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { TableSkeleton } from '@/components/ui/skeleton';
import { useProfileAuditModal } from '@/hooks/use-profile-audit-modal';
import { useProfileUsersModal } from '@/hooks/use-profile-users-modal';

export function ProfileListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Filter states
  const [filterName, setFilterName] = useState(searchParams.get('name') || '');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');

  // Modal hooks
  const auditModal = useProfileAuditModal();
  const usersModal = useProfileUsersModal();

  const page = Number(searchParams.get('page')) || 1;

  const { data, isLoading, error } = useQuery({
    queryKey: ['profiles', Object.fromEntries(searchParams)],
    queryFn: () => profileApi.findMany(Object.fromEntries(searchParams) as Record<string, string>),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => profileApi.create({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      setCreateDialogOpen(false);
      setNewProfileName('');
    },
  });

  const activeMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      profileApi.setActive(id, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('page', '1');

    if (filterName) params.set('name', filterName);

    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilterName('');
    setSearchParams(new URLSearchParams());
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleToggleActive = (profile: Profile) => {
    activeMutation.mutate({ id: profile.id, active: !profile.active });
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Erro ao carregar perfis</p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Perfis</h1>
          <p className="mt-1 text-sm text-gray-500">Gerencie os perfis de acesso da plataforma.</p>
        </div>
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="bg-black text-white hover:bg-gray-800"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Perfil
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>Preencha os filtros abaixo e clique em "Pesquisar"</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Nome"
                  placeholder="Filtrar por nome"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={10} />
        </div>
      ) : data ? (
        <ProfileTable
          data={data}
          currentPage={page}
          onPageChange={handlePageChange}
          onViewHistory={(profile) => auditModal.openModal(profile)}
          onViewUsers={(profile) => usersModal.openModal(profile)}
          onToggleActive={handleToggleActive}
        />
      ) : null}

      {/* Create Profile Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Perfil</DialogTitle>
            <DialogDescription>Digite o nome do novo perfil de acesso.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              label="Nome do perfil"
              placeholder="Ex: Administrador, Operador, etc."
              value={newProfileName}
              onChange={(e) => setNewProfileName(e.target.value)}
            />
          </div>
          {createMutation.isError && (
            <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Erro ao criar perfil'}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => createMutation.mutate(newProfileName)}
              disabled={!newProfileName.trim() || createMutation.isPending}
              className="bg-black text-white hover:bg-gray-800"
            >
              {createMutation.isPending ? 'Criando...' : 'Criar Perfil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Audit Modal */}
      <ProfileAuditModal
        open={auditModal.isOpen}
        onClose={auditModal.closeModal}
        profileName={auditModal.profile?.name || ''}
        auditData={auditModal.auditData}
        currentPage={auditModal.currentPage}
        onPageChange={auditModal.handlePageChange}
        isLoading={auditModal.isLoading}
      />

      {/* Profile Users Modal */}
      <ProfileUsersModal
        open={usersModal.isOpen}
        onClose={usersModal.closeModal}
        profileName={usersModal.profile?.name || ''}
        usersData={usersModal.usersData}
        currentPage={usersModal.currentPage}
        onPageChange={usersModal.handlePageChange}
        isLoading={usersModal.isLoading}
      />
    </motion.div>
  );
}
